import React, { useState, useCallback } from "react";
import { Box, Grid } from "@mui/material";
import SearchPanel from "../../components/documents/search-panel";
import DocumentList, {
  Folder,
  Document,
  NavigationState,
  DocumentListRefreshResult,
} from "../../components/documents/document-list";
import GroupedDocumentList, { GroupedFolder } from "../../components/documents/grouped-document-list";
import NasFolderTree from "../../components/documents/nas-folder-tree";
import ConfirmDialog from "../../components/common/confirm-dialog";
import { useApiWithHandling } from "../../hooks/useApiWithHandling";
import { useDocumentSearchFolderNavigation } from "../../hooks/useDocumentSearchFolderNavigation";
import { useSnackbar } from "../../contexts/snackbar-context";
import { useLoading } from "../../contexts/loading-context";
import { useDocumentService } from "../../services/documentService";
import { clearQuickUploadHistory } from "../../utils/documentQuickUploadHistory";
import {
  folderPathsEqual,
  resolveBrowseUrlKeys,
  toBrowseUrlKeys,
} from "../../utils/documentSearchNavigation";
import { ColdStorageFileNode, ColdStorageTree } from "../../types/coldStorage";
import {
  isNasPreviewableFile,
  NAS_VIEW_UNAVAILABLE_MESSAGE,
} from "../../utils/nasPreviewUtils";

// Define search filters interface to prevent inline type recreation
interface SearchFilters {
  documentScope: "airframe" | "assembly" | "subAssembly" | "nas";
  assemblyType: string;
  assemblyFolder: string;
  assemblyComponentId?: string;
  aircraft: string;
  nasRootFolder: string;
  documentTitle: string;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
  statuses: string[];
  withoutRegistration?: boolean;
}

// API request interface matching the backend model
interface SearchDocumentsRequest {
  TailNumber?: string;
  Category?: string;
  FromDate?: string;
  ToDate?: string;
  Filters?: Record<string, any>;
  Page: number;
  PageSize: number;
}

// API response interface matching the new backend response
interface SearchDocumentsResponse {
  data: {
    folders: Folder[];
    assemblyRootDocuments?: Document[];
    totalDocuments: number;
    totalFolders: number;
    filters: {
      tailNumber: string | null;
      category: string | null;
      fromDate: string | null;
      toDate: string | null;
    };
  };
  success: boolean;
  message: string;
  correlationId: string | null;
  timestamp: string;
}

const SearchDocumentsPage: React.FC = () => {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [allAssemblyRootDocuments, setAllAssemblyRootDocuments] = useState<Document[]>([]);
  const [assemblyRootDocuments, setAssemblyRootDocuments] = useState<Document[]>([]);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedFolder[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["completed", "inProgress", "pending"]);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isGroupedMode, setIsGroupedMode] = useState<boolean>(false);
  const [lastSearchRequest, setLastSearchRequest] =
    useState<SearchDocumentsRequest | null>(null);
  const [quickUploadHistoryEpoch, setQuickUploadHistoryEpoch] = useState(0);
  const [navigationState, setNavigationState] = useState<NavigationState>({
    currentPath: [],
    currentFolder: null,
    showingFiles: false,
  });
  const [documentPendingDelete, setDocumentPendingDelete] = useState<Document | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isDeletingDocument, setIsDeletingDocument] = useState<boolean>(false);
  const [nasTree, setNasTree] = useState<ColdStorageTree | null>(null);
  const { post, get, getBlob } = useApiWithHandling();
  const { deleteDocument } = useDocumentService();
  const { showSuccess, showError } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const {
    folderPath,
    folderGroup,
    navigateFolderPath,
    clearFolderNavigation,
  } = useDocumentSearchFolderNavigation();
  
  const waitForPaint = useCallback(async () => {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }, []);

  const findFolderByPath = useCallback((path: string[], rootFolders: Folder[]): Folder | null => {
    if (path.length === 0) {
      return null;
    }

    let currentFolders = rootFolders;
    let currentFolder: Folder | null = null;

    for (const name of path) {
      currentFolder = currentFolders.find((folder) => folder.name === name) ?? null;
      if (!currentFolder) {
        return null;
      }

      currentFolders = currentFolder.children;
    }

    return currentFolder;
  }, []);

  // Helper function to map status filter to document status
  const mapStatusToDocumentStatus = (filterStatus: string): string[] => {
    switch (filterStatus) {
      case "completed":
        return ["Approved", "Draft"];
      case "inProgress":
        return ["UnderReview"];
      case "pending":
        return ["FailedReview"];
      default:
        return [];
    }
  };

  const filterByStatus = useCallback((folders: Folder[], statuses: string[]): Folder[] => {
    const allowedStatuses = statuses.flatMap(mapStatusToDocumentStatus);
    
    const filterFolder = (folder: Folder): Folder => {
      const filteredDocuments = folder.documents.filter(doc => 
        allowedStatuses.includes(doc.status)
      );
      const filteredChildren = folder.children.map(child => filterFolder(child));
      const totalCount = filteredDocuments.length + 
        filteredChildren.reduce((sum, child) => sum + child.totalDocumentCount, 0);
      
      // Always return folder (keep structure), but with filtered documents and updated count
      return {
        ...folder,
        documents: filteredDocuments,
        children: filteredChildren,
        totalDocumentCount: totalCount,
        hasDocuments: filteredDocuments.length > 0,
        hasChildren: filteredChildren.length > 0,
      };
    };
    
    return folders.map(folder => filterFolder(folder));
  }, []);

  const filterDocumentsByStatus = useCallback((documents: Document[], statuses: string[]): Document[] => {
    const allowedStatuses = statuses.flatMap(mapStatusToDocumentStatus);
    return documents.filter((document) => allowedStatuses.includes(document.status));
  }, []);

  // Group folders by aircraft to only include folders with documents
  const groupFoldersByAircraft = useCallback((folders: Folder[]): GroupedFolder[] => {
    const grouped = new Map<string, Folder[]>();
    
    // clone a folder structure and filter documents by tail number
    const cloneFolderForAircraft = (folder: Folder, targetTailNumber: string): Folder | null => {
      // Filter documents for this tail number
      const filteredDocs = folder.documents.filter(doc => doc.tailNumber === targetTailNumber);
      
      // Recursively process children
      const filteredChildren: Folder[] = [];
      folder.children.forEach(child => {
        const clonedChild = cloneFolderForAircraft(child, targetTailNumber);
        if (clonedChild) {
          filteredChildren.push(clonedChild);
        }
      });
      
      // Calculate total document count
      const totalCount = filteredDocs.length + 
        filteredChildren.reduce((sum, child) => sum + child.totalDocumentCount, 0);
      
      // Only return folder if it has documents (directly or in children)
      if (totalCount === 0) {
        return null;
      }
      
      return {
        ...folder,
        documents: filteredDocs,
        children: filteredChildren,
        totalDocumentCount: totalCount,
      };
    };
    
    // Collect all unique tail numbers
    const tailNumbers = new Set<string>();
    const collectTailNumbers = (folder: Folder): void => {
      folder.documents.forEach(doc => {
        if (doc.tailNumber) {
          tailNumbers.add(doc.tailNumber);
        }
      });
      folder.children.forEach(child => collectTailNumbers(child));
    };
    folders.forEach(folder => collectTailNumbers(folder));
    
    // For each tail number, create a filtered folder tree
    tailNumbers.forEach(tailNumber => {
      const filteredFolders: Folder[] = [];
      folders.forEach(folder => {
        const cloned = cloneFolderForAircraft(folder, tailNumber);
        if (cloned) {
          filteredFolders.push(cloned);
        }
      });
      
      if (filteredFolders.length > 0) {
        grouped.set(tailNumber, filteredFolders);
      }
    });
    
    // Convert to array format
    return Array.from(grouped.entries())
      .map(([tailNumber, folders]) => {
        const totalDocuments = folders.reduce((sum, f) => sum + f.totalDocumentCount, 0);
        return { tailNumber, folders, totalDocuments };
      })
      .filter(group => group.totalDocuments > 0)
      .sort((a, b) => a.tailNumber.localeCompare(b.tailNumber));
  }, []);

  const handleSearch = useCallback(
    async (filters: SearchFilters) => {
      clearQuickUploadHistory();
      setQuickUploadHistoryEpoch((e) => e + 1);

      if (filters.documentScope === "nas") {
        try {
          showLoading("Loading NAS folders from blob storage. Please wait...");
          setIsGroupedMode(false);
          setLastSearchRequest(null);
          setFolders([]);
          setAllFolders([]);
          setAllAssemblyRootDocuments([]);
          setAssemblyRootDocuments([]);
          setGroupedData([]);
          clearFolderNavigation({ replace: true });
          setNavigationState({
            currentPath: [],
            currentFolder: null,
            showingFiles: false,
          });

          await get<ColdStorageTree>(
            `api/cold-storage/tree?rootFolder=${encodeURIComponent(filters.nasRootFolder)}`,
            {
              showLoader: false,
              showErrorSnackbar: true,
              showSuccessSnackbar: false,
              onSuccess: (response) => {
                const tree = response?.data ?? response;
                if (tree?.root) {
                  setNasTree(tree);
                  setHasSearched(true);
                  return;
                }

                setNasTree(null);
                setHasSearched(false);
              },
              onError: () => {
                setNasTree(null);
                setHasSearched(false);
              },
            }
          );
        } catch {
          setNasTree(null);
          setHasSearched(false);
        } finally {
          await waitForPaint();
          hideLoading();
        }
        return;
      }

      setNasTree(null);

      // Determine if this is grouped mode (search without registration)
      const isGrouped =
        filters.documentScope === "airframe" &&
        filters.withoutRegistration === true;
      
      // Prepare the request payload matching the backend API
      const searchRequest: SearchDocumentsRequest = {
        Page: 1,
        PageSize: 1000,
        ...(filters.documentScope === "airframe" &&
          filters.aircraft &&
          !isGrouped && { TailNumber: filters.aircraft }),
        ...(filters.startDate && { FromDate: filters.startDate.toISOString() }),
        ...(filters.endDate && { ToDate: filters.endDate.toISOString() }),
        Filters: {
          documentScope: filters.documentScope,
          ...(filters.documentScope === "assembly" &&
            filters.assemblyType && { assemblyType: filters.assemblyType }),
          ...(filters.documentScope === "assembly" &&
            filters.assemblyFolder && { assemblyFolder: filters.assemblyFolder }),
          ...(filters.documentScope === "assembly" &&
            filters.assemblyComponentId && { componentId: filters.assemblyComponentId }),
          ...(filters.documentScope === "subAssembly" && {
            assemblyType: "landingGear",
          }),
          ...(filters.documentScope === "subAssembly" &&
            filters.assemblyFolder && { assemblyFolder: filters.assemblyFolder }),
          ...(filters.documentScope === "subAssembly" &&
            filters.assemblyComponentId && { componentId: filters.assemblyComponentId }),
          ...(filters.documentTitle && { title: filters.documentTitle }),
          ...(filters.description && { description: filters.description }),
        },
      };
      try {
        showLoading("Loading documents from all folders. Please wait...");
        setLastSearchRequest(searchRequest);
        setSelectedStatuses(filters.statuses);
        setIsGroupedMode(isGrouped);
        
        await post<SearchDocumentsResponse>(
          "api/documents/search",
          searchRequest,
          {
            showLoader: false,
            showErrorSnackbar: true,
            showSuccessSnackbar: false,
            onSuccess: (response) => {
              if (response.success && response.data?.folders) {
                setAllFolders(response.data.folders);
                setAllAssemblyRootDocuments(response.data.assemblyRootDocuments ?? []);
                const filtered = filterByStatus(response.data.folders, filters.statuses);
                setAssemblyRootDocuments(
                  filterDocumentsByStatus(response.data.assemblyRootDocuments ?? [], filters.statuses)
                );
                
                if (isGrouped) {
                  // Group by aircraft and only show folders with documents
                  const grouped = groupFoldersByAircraft(filtered);
                  setGroupedData(grouped);
                } else {
                  setFolders(filtered);
                }
                
                setHasSearched(true);
                // New search starts at root; clear folder history from the URL.
                clearFolderNavigation({ replace: true });
                setNavigationState({
                  currentPath: [],
                  currentFolder: null,
                  showingFiles: false,
                });
              } else {
                setFolders([]);
                setAllFolders([]);
                setAllAssemblyRootDocuments([]);
                setAssemblyRootDocuments([]);
                setGroupedData([]);
                setHasSearched(false);
                clearFolderNavigation({ replace: true });
              }
            },
            onError: (error) => {
              setFolders([]);
              setAllFolders([]);
              setGroupedData([]);
              setHasSearched(false);
              clearFolderNavigation({ replace: true });
              setNavigationState({
                currentPath: [],
                currentFolder: null,
                showingFiles: false,
              });
            },
          }
        );
      } catch (error) {
        setFolders([]);
        setAllFolders([]);
        setGroupedData([]);
        setHasSearched(false);
        clearFolderNavigation({ replace: true });
        setNavigationState({
          currentPath: [],
          currentFolder: null,
          showingFiles: false,
        });
      } finally {
        await waitForPaint();
        hideLoading();
      }
    },
    [
      post,
      filterByStatus,
      filterDocumentsByStatus,
      groupFoldersByAircraft,
      showLoading,
      hideLoading,
      waitForPaint,
      clearFolderNavigation,
      get,
    ]
  );

  // Update folders state when filtered results change
  React.useEffect(() => {
    if (allFolders.length > 0) {
      const filtered = filterByStatus(allFolders, selectedStatuses);
      setAssemblyRootDocuments(filterDocumentsByStatus(allAssemblyRootDocuments, selectedStatuses));
      
      if (isGroupedMode) {
        const grouped = groupFoldersByAircraft(filtered);
        setGroupedData(grouped);
      } else {
        setFolders(filtered);
      }
    }
  }, [selectedStatuses, allFolders, allAssemblyRootDocuments, filterByStatus, filterDocumentsByStatus, isGroupedMode, groupFoldersByAircraft]);

  // URL is the source of truth for folder depth (browser Back/Forward).
  // URL segments are codes/ids; navigationState.currentPath stays display names.
  React.useEffect(() => {
    if (isGroupedMode || nasTree) {
      return;
    }

    if (!hasSearched) {
      setNavigationState((previousState) => {
        if (
          previousState.currentPath.length === 0 &&
          previousState.currentFolder === null &&
          previousState.showingFiles === false
        ) {
          return previousState;
        }

        return {
          currentPath: [],
          currentFolder: null,
          showingFiles: false,
        };
      });
      return;
    }

    const resolved = resolveBrowseUrlKeys(folderPath, folders);

    if (
      folders.length > 0 &&
      !resolved.ambiguous &&
      !folderPathsEqual(resolved.urlKeys, folderPath)
    ) {
      // Canonicalize legacy name-based URLs onto code/id keys.
      // Skip when a segment is ambiguous (duplicate codes) so we never pick
      // the first sibling (e.g. LMLG instead of RMLG).
      navigateFolderPath(resolved.urlKeys, { replace: true });
      return;
    }

    const currentFolder =
      resolved.namePath.length > 0
        ? findFolderByPath(resolved.namePath, folders)
        : null;
    const showingFiles = currentFolder ? currentFolder.documents.length > 0 : false;

    setNavigationState((previousState) => {
      if (
        folderPathsEqual(previousState.currentPath, resolved.namePath) &&
        previousState.currentFolder === currentFolder &&
        previousState.showingFiles === showingFiles
      ) {
        return previousState;
      }

      return {
        currentPath: resolved.namePath,
        currentFolder,
        showingFiles,
      };
    });
  }, [
    folderPath,
    folders,
    findFolderByPath,
    hasSearched,
    isGroupedMode,
    navigateFolderPath,
    nasTree,
  ]);

  const handleToggleFolder = useCallback((idx: number) => {
    // Not needed anymore, handled internally by DocumentList
  }, []);

  const handleNavigateToFolder = useCallback(
    (folderName: string) => {
      const nextPath = [...navigationState.currentPath, folderName];
      const nextFolder = findFolderByPath(nextPath, folders);

      setNavigationState({
        currentPath: nextPath,
        currentFolder: nextFolder,
        showingFiles: nextFolder ? nextFolder.documents.length > 0 : false,
      });
      navigateFolderPath(toBrowseUrlKeys(nextPath, folders));
    },
    [findFolderByPath, folders, navigateFolderPath, navigationState.currentPath]
  );

  const handleNavigateBack = useCallback(
    (path: string[], options?: { replace?: boolean }) => {
      const nextFolder = path.length > 0 ? findFolderByPath(path, folders) : null;

      setNavigationState({
        currentPath: path,
        currentFolder: nextFolder,
        showingFiles: nextFolder ? nextFolder.documents.length > 0 : false,
      });
      navigateFolderPath(toBrowseUrlKeys(path, folders), {
        replace: options?.replace === true,
      });
    },
    [findFolderByPath, folders, navigateFolderPath]
  );

  const handleViewDocument = useCallback((doc: Document) => {
    // Implement document viewing logic
    
  }, []);

  const handleDownloadDocument = useCallback((doc: Document) => {
    // Implement download logic
    //alert(`Download: ${doc.fileName}`);
  }, []);

  const downloadNasFile = useCallback(
    async (file: ColdStorageFileNode, openInBrowser: boolean) => {
      if (openInBrowser && !isNasPreviewableFile(file.name)) {
        showError(NAS_VIEW_UNAVAILABLE_MESSAGE);
        return;
      }

      await getBlob(
        `api/cold-storage/download?rootFolder=${encodeURIComponent(nasTree?.root ?? "")}&relativePath=${encodeURIComponent(file.path)}`,
        {
          showLoader: true,
          showErrorSnackbar: true,
          showSuccessSnackbar: false,
          loadingMessage: openInBrowser ? "Opening file..." : "Downloading file...",
          onSuccess: (blob, contentType, fileName) => {
            const url = window.URL.createObjectURL(blob);
            if (openInBrowser) {
              if (isNasPreviewableFile(fileName || file.name, contentType)) {
                window.open(url, "_blank", "noopener,noreferrer");
                return;
              }

              window.URL.revokeObjectURL(url);
              showError(NAS_VIEW_UNAVAILABLE_MESSAGE);
              return;
            }

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || file.name;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showSuccess(`File "${file.name}" downloaded successfully`);
          },
        }
      );
    },
    [getBlob, nasTree?.root, showError, showSuccess]
  );

  const handleDocumentDelete = useCallback((doc: Document) => {
    setDocumentPendingDelete(doc);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    if (isDeletingDocument) {
      return;
    }

    setIsDeleteDialogOpen(false);
    setDocumentPendingDelete(null);
  }, [isDeletingDocument]);

  const handleStatusChange = useCallback((statuses: string[]) => {
    setSelectedStatuses(statuses);
  }, []);

  // Refresh handler for document list - re-executes the last search
  const handleRefresh = useCallback(async (): Promise<DocumentListRefreshResult | null> => {
    if (!lastSearchRequest) {
      return null;
    }

    try {
      return await new Promise<DocumentListRefreshResult | null>((resolve) => {
        void post<SearchDocumentsResponse>(
          "api/documents/search",
          lastSearchRequest,
          {
            showLoader: false,
            showErrorSnackbar: true,
            showSuccessSnackbar: false,
            onSuccess: (response) => {
              if (response.success && response.data?.folders) {
                const nextAllFolders = response.data.folders;
                const nextAssemblyRootDocuments =
                  response.data.assemblyRootDocuments ?? [];
                setAllFolders(nextAllFolders);
                setAllAssemblyRootDocuments(nextAssemblyRootDocuments);
                const filtered = filterByStatus(nextAllFolders, selectedStatuses);
                const filteredAssemblyRootDocuments = filterDocumentsByStatus(
                  nextAssemblyRootDocuments,
                  selectedStatuses
                );
                setAssemblyRootDocuments(filteredAssemblyRootDocuments);

                if (isGroupedMode) {
                  const grouped = groupFoldersByAircraft(filtered);
                  setGroupedData(grouped);
                } else {
                  setFolders(filtered);
                }

                resolve({
                  folders: nextAllFolders,
                  assemblyRootDocuments: nextAssemblyRootDocuments,
                });
                return;
              }

              resolve(null);
            },
            onError: () => {
              resolve(null);
            },
          }
        );
      });
    } catch {
      return null;
    }
  }, [lastSearchRequest, post, filterByStatus, filterDocumentsByStatus, selectedStatuses, isGroupedMode, groupFoldersByAircraft]);

  const handleConfirmDelete = useCallback(async () => {
    if (!documentPendingDelete?.id) {
      return;
    }

    try {
      setIsDeletingDocument(true);

      const isDeleted = await deleteDocument(documentPendingDelete.id);
      if (!isDeleted) {
        return;
      }

      showSuccess("Document deleted successfully");
      await handleRefresh();
      setIsDeleteDialogOpen(false);
      setDocumentPendingDelete(null);
    } finally {
      setIsDeletingDocument(false);
    }
  }, [deleteDocument, documentPendingDelete, handleRefresh, showSuccess]);

  // Reset results when switching modes
  const handleClearResults = useCallback(() => {
    setFolders([]);
    setGroupedData([]);
    setAllFolders([]);
    setNasTree(null);
    setHasSearched(false);
    clearFolderNavigation({ replace: true });
    setNavigationState({
      currentPath: [],
      currentFolder: null,
      showingFiles: false,
    });
  }, [clearFolderNavigation]);

  return (
    <Box sx={{ height: "auto", width: "auto", p: { xs: 1, sm: 2 } }}>
      <Grid container spacing={2} sx={{ 
        flexDirection: { xs: "column", md: "row" },
        minHeight: { md: "60vh" },
      }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <SearchPanel 
            onSearch={handleSearch} 
            onStatusChange={handleStatusChange} 
            onClearResults={handleClearResults}
          />
        </Grid>
        {hasSearched && (
          <Grid size={{ xs: 12, md: 9 }}>
            {nasTree ? (
              <NasFolderTree
                key={nasTree.root}
                tree={nasTree}
                onViewFile={(file) => {
                  void downloadNasFile(file, true);
                }}
                onDownloadFile={(file) => {
                  void downloadNasFile(file, false);
                }}
              />
            ) : isGroupedMode ? (
              <GroupedDocumentList
                groupedData={groupedData}
                onViewDocument={handleViewDocument}
                onDownloadDocument={handleDownloadDocument}
                onDeleteDocument={handleDocumentDelete}
                lastSearchRequest={lastSearchRequest}
                onRefresh={handleRefresh}
                quickUploadHistoryEpoch={quickUploadHistoryEpoch}
                folderPath={folderPath}
                folderGroup={folderGroup}
                onNavigateFolderPath={navigateFolderPath}
              />
            ) : (
              <DocumentList
                folders={folders}
                allFolders={allFolders}
                assemblyRootDocuments={assemblyRootDocuments}
                navigationState={navigationState}
                onToggleFolder={handleToggleFolder}
                onNavigateToFolder={handleNavigateToFolder}
                onNavigateBack={handleNavigateBack}
                onViewDocument={handleViewDocument}
                onDownloadDocument={handleDownloadDocument}
                onDeleteDocument={handleDocumentDelete}
                lastSearchRequest={lastSearchRequest}
                onRefresh={handleRefresh}
                quickUploadHistoryEpoch={quickUploadHistoryEpoch}
              />
            )}
          </Grid>
        )}
        {/* <Grid size={{ xs: 9 }} sx={{ pl:2,  flexGrow:1 }}>
					<DocumentViewer selectedDoc={selectedDoc} onClose={handleCloseViewer} />
				</Grid> */}
      </Grid>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete document"
        message={
          <Box sx={{ mt: 1 }}>
            <Box component="p" sx={{ color: "#ccc", m: 0, lineHeight: 1.7 }}>
              Are you sure you want to delete? This Action cant be undone Easily.
            </Box>
            <Box component="p" sx={{ color: "#ccc", m: 0, mt: 1.5, lineHeight: 1.7 }}>
              We Never Loose any document even Deleted, Contact Admin to recover the archives
            </Box>
          </Box>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDangerous
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeletingDocument}
      />
    </Box>
  );
};

export default SearchDocumentsPage;
